import React, { useState } from "react";
import {
    FaArrowRight, FaCamera, FaShieldAlt, FaGlobe, FaShareAlt,
    FaTimes, FaCheckCircle, FaFilePdf, FaSearch, FaExclamationTriangle,
    FaBuilding, FaUniversity, FaGlobeAfrica, FaClipboardList,
    FaChevronDown, FaChevronUp, FaAward, FaBolt,
    FaMapMarkerAlt, FaFingerprint, FaChartLine, FaBarcode, FaFileAlt, FaCloud,
    FaCertificate,
} from "react-icons/fa";
import HeaderAny from "../../components/header";
import MobileNav from "../../components/header-mobile";
import Footer from "../../components/footer";
import buildingImg from "../../assets/con-hero-crane.jpg";
import "./solutions.css";
import "./contractors.css";

const GET_STARTED_URL = "https://web.conra.net";
const PROJECT_CODE = "KN21G";

const PROBLEMS = [
    {
        Icon: FaFilePdf,
        title: "Heavy and slow",
        desc: "PDF profiles are slow to design, expensive to print, and painful to update every time a milestone is hit. By the time the brochure is ready, it's already behind reality.",
    },
    {
        Icon: FaExclamationTriangle,
        title: "Not verifiable",
        desc: "Anyone can copy an image from the internet, another site, or a competitor's project and present it as their own. There's no built-in signal that a photo is genuinely yours.",
    },
    {
        Icon: FaSearch,
        title: "Nothing to check independently",
        desc: "A client, bank, or tender board can't confirm anything from a static portfolio without visiting the site in person. Which usually means they simply don't confirm.",
    },
];

const COMPARE_ROWS = [
    {
        label: "SETUP COST",
        traditional: "Design and printing fees, often outsourced.",
        conra: "Built automatically from your site photos.",
    },
    {
        label: "UPDATE SPEED",
        traditional: "Manual redesign every time something changes.",
        conra: "Instant, the moment a photo is taken.",
    },
    {
        label: "VERIFIABILITY",
        traditional: "None — images can be copied from anywhere.",
        conra: "Cryptographically time- and location-stamped.",
    },
    {
        label: "ACCESS",
        traditional: "Email attachment, physical copy, or download link.",
        conra: "One shareable project code.",
    },
    {
        label: "BEST FOR",
        traditional: "A snapshot in time.",
        conra: "A living, ongoing record.",
    },
    {
        label: "MAINTENANCE",
        traditional: "Falls on you or a designer.",
        conra: "None required.",
    },
];

const ADVANTAGES = [
    {
        Icon: FaShieldAlt,
        title: "Build stronger client trust",
        desc: "Clients are naturally skeptical of progress updates — they've been burned before by delays dressed up as “almost done.” With ConRa, clients aren't taking your word for it. They're seeing verified location and time data behind every photo, whenever they choose to look.",
    },
    {
        Icon: FaUniversity,
        title: "Unlock faster bank loans",
        desc: "Capital delays kill momentum on-site. Instead of waiting on a bank's schedule for a physical surveyor to visit and sign off before releasing the next loan tranche, share your ConRa project code. Financial institutions can review verified, real-time progress remotely and move toward releasing funds far more quickly.",
    },
    {
        Icon: FaAward,
        title: "Stand out in tenders",
        desc: "Tender boards read the same claims from every bidder: “experienced team,” “proven track record,” “on-time delivery.” Words are cheap. Attach your ConRa portfolio codes to your bid, and your past work speaks for itself — a verifiable, tamper-evident history that competitors submitting PDFs simply can't match.",
    },
    {
        Icon: FaGlobe,
        title: "Seamless cross-border collaboration",
        desc: "Diaspora buyers and international stakeholders can't pop by the site to check progress themselves. A ConRa code gives them a secure, standing window into their investment — accurate, current, and checkable on their own schedule, in their own time zone.",
    },
    {
        Icon: FaBolt,
        title: "Easier than having a website",
        desc: "A website needs a designer, a developer, hosting, and constant upkeep — a real cost and a real distraction from the actual build. With ConRa, the work builds the portfolio for you. Every photo your crew takes becomes part of a permanent, presentable record. No code, no web designer, no maintenance — just build, and let ConRa handle the rest.",
    },
];

const STEPS = [
    { Icon: FaCamera, num: "STEP 01", title: "Capture", desc: "Your crew takes a progress photo on-site, exactly as they do today. No new hardware, no complex training." },
    { Icon: FaShieldAlt, num: "STEP 02", title: "Verify", desc: "ConRa embeds location, timestamp, and authenticity data at the moment of capture — aligned with the C2PA content-provenance standard." },
    { Icon: FaGlobe, num: "STEP 03", title: "Publish", desc: "The photo is added to your project's live timeline automatically. No uploading, no formatting, no design work." },
    { Icon: FaShareAlt, num: "STEP 04", title: "Share", desc: "Anyone with your project code can view the verified history, anytime, from anywhere in the world." },
];

const STAKEHOLDERS = [
    { Icon: FaBuilding, title: "Contractors & Developers", desc: "Turn day-to-day site work into a standing, sellable track record that follows your firm across projects." },
    { Icon: FaUniversity, title: "Banks & Financial Institutions", desc: "Verify progress remotely before releasing loan tranches, replacing weeks of surveyor scheduling with a permission-based lookup." },
    { Icon: FaClipboardList, title: "Tender & Procurement Boards", desc: "Evaluate bidders on verifiable history rather than paperwork claims — reducing the risk of awarding work to a firm that can't actually back its promises." },
    { Icon: FaGlobeAfrica, title: "Diaspora & International Investors", desc: "Monitor a project's real progress from anywhere in the world, without depending on WhatsApp updates or a contact on the ground." },
];

const WHEEL_ITEMS = [
    { label: "Geo-Tagged\nMedia", angle: 270 },
    { label: "Content Authenticity\n(C2PA)", angle: 330 },
    { label: "Time & Location\nStamped", angle: 30 },
    { label: "Company Unique\nCode (KN21G)", angle: 90 },
    { label: "Immutable Audit\nTrail", angle: 150 },
    { label: "Secure Cloud\nStorage", angle: 210 },
];

/* Spoke diagram – pure SVG (mirrors the Banks page wheel) */
const WheelDiagram = () => {
    const cx = 160; const cy = 160; const r = 110; const dotR = 6;
    const toRad = (deg) => (deg * Math.PI) / 180;
    return (
        <svg viewBox="0 0 320 320" width="320" height="320" className="con-wheel-svg">
            {WHEEL_ITEMS.map(({ angle }) => {
                const x = cx + r * Math.cos(toRad(angle));
                const y = cy + r * Math.sin(toRad(angle));
                return <line key={angle} x1={cx} y1={cy} x2={x} y2={y} stroke="#dbeafe" strokeWidth="1.5" />;
            })}
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="#dbeafe" strokeWidth="1" />
            <circle cx={cx} cy={cy} r={40} fill="var(--color-primary)" />
            <text x={cx} y={cy - 6} textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff">ConRa</text>
            <text x={cx} y={cy + 10} textAnchor="middle" fontSize="10" fill="rgba(255,255,255,0.75)">Security</text>
            {WHEEL_ITEMS.map(({ label, angle }) => {
                const x = cx + r * Math.cos(toRad(angle));
                const y = cy + r * Math.sin(toRad(angle));
                const lines = label.split("\n");
                const isRight = x > cx + 20;
                const isLeft = x < cx - 20;
                const anchor = isRight ? "start" : isLeft ? "end" : "middle";
                const lx = isRight ? x + 12 : isLeft ? x - 12 : x;
                return (
                    <g key={angle}>
                        <circle cx={x} cy={y} r={dotR + 4} fill="#eff6ff" />
                        <circle cx={x} cy={y} r={dotR} fill="var(--color-primary)" />
                        {lines.map((line, i) => (
                            <text
                                key={i}
                                x={lx}
                                y={y + (i - (lines.length - 1) / 2) * 14}
                                textAnchor={anchor}
                                fontSize="10"
                                fontWeight="600"
                                fill="#374151"
                                dominantBaseline="middle"
                            >{line}</text>
                        ))}
                    </g>
                );
            })}
        </svg>
    );
};

const ContractorsPage = () => {
    return (
        <div>
            <div className="web-only"><HeaderAny /></div>
            <div className="mobile-only"><MobileNav /></div>

            {/* ── Hero ── */}
            <section className="con-hero-wrap">
                <div className="con-hero-inner">
                    <div className="con-hero-text">
                        <p className="con-hero-breadcrumb">Home / For Contractors &amp; Developers</p>
                        <p className="con-hero-overtitle">FOR CONTRACTORS &amp; DEVELOPERS</p>
                        <h1 className="con-hero-h1">
                            Proof of progress.<br />Not just promises.
                        </h1>
                        <p className="con-hero-sub">
                            Every contractor has a folder of finished-project photos somewhere — on a phone, in a
                            WhatsApp thread, buried in an old PDF nobody opens twice. That folder represents years
                            of real work. But the moment it's compiled into a static brochure, it stops proving
                            anything. ConRa closes that gap.
                        </p>
                        <div className="con-hero-actions">
                            <button className="filled-button" onClick={() => window.open(GET_STARTED_URL, "_blank")}>
                                Claim your firm's profile
                            </button>
                            <button className="sol-outlined-button" onClick={() => window.open(GET_STARTED_URL, "_blank")}>
                                See the old way vs. the new way
                            </button>
                        </div>
                    </div>
                    <div className="con-hero-visual">
                        <div className="con-hero-img-wrap">
                            <img src={buildingImg} alt="Tower crane against a sunset sky at a construction site" className="con-hero-img" />
                        </div>
                        <div className="con-hero-stats">
                            <div className="con-hero-stats-top">
                                <span className="con-hero-verified-pill">VERIFIED</span>
                                <span className="con-hero-stat-num">21</span>
                            </div>
                            <div className="con-hero-stat-divider" />
                            <p className="con-hero-stat-caption">
                                KNIGHTS ENGINEERING &middot; {"{"}{PROJECT_CODE}{"}"} &middot; 47 VERIFIED PROJECTS
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Problem ── */}
            <section className="con-problem-wrap">
                <div className="con-problem-inner">
                    <p className="con-problem-overtitle">THE PROBLEM WITH HOW PROGRESS IS COMMUNICATED TODAY</p>
                    <h2 className="con-problem-h2">
                        The static PDF portfolio was never a design problem.{" "}
                        <span>It's a trust problem.</span>
                    </h2>
                    <p className="con-problem-sub">
                        In construction, trust is what releases payments, wins bids, and keeps investors calm.
                        And traditional portfolios don't build any.
                    </p>
                    <div className="con-problem-cards">
                        {PROBLEMS.map(({ Icon, title, desc }) => (
                            <div key={title} className="con-problem-card">
                                <span className="con-problem-icon"><Icon /></span>
                                <h4 className="con-problem-card-title">{title}</h4>
                                <p className="con-problem-card-desc">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Compare ── */}
            <section className="con-compare-wrap">
                <div className="con-compare-inner">
                    <span className="con-compare-badge">OLD VS. NEW</span>
                    <h2 className="con-compare-h2">ConRa vs. the traditional portfolio.</h2>
                    <p className="con-compare-sub">
                        Side by side, on the things that actually matter to your day-to-day.
                    </p>
                    <div className="con-table">
                        <div className="con-table-header">
                            <div className="con-table-label-col" />
                            <div className="con-table-col-head con-col-old">Traditional PDF</div>
                            <div className="con-table-col-head con-col-new">ConRa</div>
                        </div>
                        {COMPARE_ROWS.map(({ label, traditional, conra }, i) => (
                            <div key={label} className={`con-table-row ${i % 2 === 1 ? "con-row-alt" : ""}`}>
                                <div className="con-table-label">{label}</div>
                                <div className="con-table-cell con-cell-old">
                                    <FaTimes className="con-cell-x" />
                                    <span>{traditional}</span>
                                </div>
                                <div className="con-table-cell con-cell-new">
                                    <FaCheckCircle className="con-cell-check" />
                                    <span>{conra}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Five Advantages ── */}
            <section className="con-adv-wrap">
                <div className="con-adv-inner">
                    <p className="con-adv-overtitle">FIVE GAME-CHANGING ADVANTAGES</p>
                    <h2 className="con-adv-h2">
                        What a verified portfolio actually changes for your business.
                    </h2>
                    <ul className="con-adv-list">
                        {ADVANTAGES.map(({ Icon, title, desc }) => (
                            <li key={title} className="con-adv-item">
                                <Icon className="con-adv-icon" />
                                <div className="con-adv-body">
                                    <h4 className="con-adv-title">{title}</h4>
                                    <p className="con-adv-desc">{desc}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* ── How It Works ── */}
            <section className="con-steps-wrap">
                <span className="con-steps-bg-shape" aria-hidden="true" />
                <div className="con-steps-inner">
                    <p className="con-steps-overtitle">HOW IT WORKS</p>
                    <h2 className="con-steps-h2">
                        Four steps between a photo and a portfolio your bank can trust.
                    </h2>

                    {/* Phone mockup */}
                    <div className="con-phone-wrap">
                        <div className="con-phone">
                            <div className="con-phone-screen">
                                <div className="con-phone-topbar">
                                    <span className="con-phone-dot" />
                                    <span className="con-phone-dot" />
                                    <span className="con-phone-dot" />
                                </div>
                                <div className="con-phone-content">
                                    <div className="con-phone-badge">✓ C2PA Verified</div>
                                    <div className="con-phone-line" />
                                    <div className="con-phone-line con-phone-line--short" />
                                    <div className="con-phone-img-placeholder" />
                                    <div className="con-phone-line" />
                                    <div className="con-phone-line con-phone-line--short" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="con-steps-grid">
                        {STEPS.map(({ Icon, num, title, desc }, i) => (
                            <React.Fragment key={num}>
                                <div className="con-step">
                                    <div className="con-step-icon-wrap"><Icon /></div>
                                    <span className="con-step-num">{num}</span>
                                    <h4 className="con-step-title">{title}</h4>
                                    <p className="con-step-desc">{desc}</p>
                                </div>
                                {i < STEPS.length - 1 && (
                                    <div className="con-step-arrow" aria-hidden="true"><FaArrowRight /></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Stakeholders ── */}
            <section className="con-stake-wrap">
                <div className="con-stake-inner">
                    <p className="con-stake-overtitle">BUILT FOR EVERY STAKEHOLDER IN THE BUILD</p>
                    <h2 className="con-stake-h2">One code, four different reasons to trust it.</h2>
                    <div className="con-stake-grid">
                        {STAKEHOLDERS.map(({ Icon, title, desc }) => (
                            <div key={title} className="con-stake-card">
                                <span className="con-stake-icon"><Icon /></span>
                                <h4 className="con-stake-title">{title}</h4>
                                <p className="con-stake-desc">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Security ── */}
            <section className="con-sec-wrap">
                <div className="con-sec-inner">
                    <div className="con-sec-chart">
                        <p className="con-sec-overtitle">SECURITY YOU CAN RELY ON</p>
                        <h2 className="con-sec-h2">
                            Locked at capture.<br />Controlled by your<br />project code.
                        </h2>
                        <WheelDiagram />
                    </div>
                    <div className="con-sec-text">
                        <p className="con-sec-body">
                            Every image on ConRa is locked with tamper-evident metadata at the moment of capture.
                            Once it's part of a project's timeline, altering or backdating it becomes considerably harder
                            — not because we say so, but because the underlying standard (C2PA) is designed to catch it.
                        </p>
                        <p className="con-sec-body">
                            Access is controlled entirely by the project code. Nothing on your portfolio is publicly
                            searchable. You decide who a code is shared with, and you can revoke that access when a
                            project ends.
                        </p>
                        <div className="con-sec-badges">
                            <div className="con-sec-badge">
                                <span className="con-sec-badge-icon"><FaCertificate /></span>
                                <div>
                                    <span className="con-sec-badge-title con-sec-badge-title--blue">C2PA</span>
                                    <span className="con-sec-badge-sub">Active member — Coalition for Content Provenance and Authenticity</span>
                                </div>
                            </div>
                            <div className="con-sec-badge">
                                <div>
                                    <span className="con-sec-badge-title">KGBS</span>
                                    <span className="con-sec-badge-sub">Active member — Kenya Green Building Society, established WorldGBC member</span>
                                </div>
                            </div>
                            <div className="con-sec-badge">
                                <div>
                                    <span className="con-sec-badge-title">KIPI</span>
                                    <span className="con-sec-badge-sub">Provisional patent filed — verification methodology</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ContractorsPage;
